/* Plan passes, the 14-day withdrawal window and where the student is on their path. */
import { EVERYTHING_FREE, planById, type Application, type Pass } from '@pathabroad/core/browser';

const t = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);

/* paid and not run out */
export const isActive = (p: Pass, now: number = Date.now()) =>
  p.status === 'paid' && (p.valid_until == null || !(t(p.valid_until) <= now));

export const activePasses = (passes: Pass[], now: number = Date.now()) => passes.filter(p => isActive(p, now));

/* "Withdraw from contract here" shows only while a paid pass is inside its 14 days */
export const canWithdraw = (p: Pass, now: number = Date.now()) => p.status === 'paid' && t(p.refundable_until) > now;

export type PassState = 'active' | 'expired' | 'refunded' | 'canceled';
export function passState(p: Pass, now: number = Date.now()): PassState {
  if (p.status === 'refunded') return 'refunded';
  if (p.status === 'canceled') return 'canceled';
  return isActive(p, now) ? 'active' : 'expired';
}

/* After Stripe sends the student back (?paid=1) we wait for the webhook to add the pass: a pass paid since the
   student left for checkout. Allows for some clock difference between the browser and the server. */
export function freshPass(passes: Pass[], since: number, skewMs = 15 * 60 * 1000): Pass | null {
  const fresh = passes.filter(p => p.status === 'paid' && t(p.paid_at) >= since - skewMs);
  fresh.sort((a, b) => t(b.paid_at) - t(a.paid_at));
  return fresh[0] ?? null;
}

/* which paid plans the student already has for this season */
export function covered(passes: Pass[], now: number = Date.now()) {
  if (EVERYTHING_FREE) return { apply: true, arrive: true };   // everything is free since 3 November 2026
  const live = activePasses(passes, now);
  const has = (g: 'apply' | 'arrive') => live.some(p => planById(p.plan).grants.includes(g));
  return { apply: has('apply'), arrive: has('arrive') };
}

/* the latest valid-until date among active passes that give this plan */
export function coverUntil(passes: Pass[], plan: 'apply' | 'arrive' | 'bundle', now: number = Date.now()): string | null {
  const grants = planById(plan).grants;
  const live = activePasses(passes, now).filter(p => grants.every(g => planById(p.plan).grants.includes(g)) || p.plan === plan);
  const dates = live.map(p => p.valid_until).filter((d): d is string => !!d).sort();
  return dates.length ? dates[dates.length - 1] : null;
}

/* the four stops on the path: 0 profile, 1 apply, 2 admit (waiting for or holding decisions), 3 arrive */
export type Phase = 0 | 1 | 2 | 3;
export function currentPhase(apps: Application[]): Phase {
  if (apps.some(a => a.status === 'accepted')) return 3;
  if (apps.some(a => a.status === 'submitted' || a.status.startsWith('decision_'))) return 2;
  if (apps.some(a => a.status === 'planned' || a.status === 'in_progress')) return 1;
  return 0;
}
